import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useAuth } from './AuthContext';
function hexToHsl(hex) {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex);
    if (!m)
        return null;
    const n = parseInt(m[1], 16);
    const r = ((n >> 16) & 255) / 255;
    const g = ((n >> 8) & 255) / 255;
    const b = (n & 255) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
        h /= 6;
    }
    return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}
const TenantContext = createContext(undefined);
const STORAGE_KEY = 'civille:currentTenantId';
export function TenantProvider({ children }) {
    const { user } = useAuth();
    const [currentTenantId, setCurrentTenantId] = useState(() => localStorage.getItem(STORAGE_KEY));
    const { data: tenants = [], isLoading } = useQuery({
        queryKey: ['tenants', user?.sub],
        queryFn: () => api.tenants.list(),
        enabled: !!user && !user.mustChangePassword && !user.twoFactorSetupRequired,
    });
    const currentTenant = tenants.find((t) => t.id === currentTenantId) ?? tenants[0] ?? null;
    useEffect(() => {
        if (currentTenant && currentTenant.id !== currentTenantId) {
            setCurrentTenantId(currentTenant.id);
            localStorage.setItem(STORAGE_KEY, currentTenant.id);
        }
    }, [currentTenant, currentTenantId]);
    const switchTenant = useCallback((id) => {
        setCurrentTenantId(id);
        localStorage.setItem(STORAGE_KEY, id);
    }, []);
    const role = user?.isSuperAdmin
        ? 'SUPER_ADMIN'
        : user?.platformRole === 'SUPPORT'
            ? 'SUPPORT'
            : (user?.memberships.find((m) => m.tenantId === currentTenant?.id)?.role ?? null);
    const branded = !!currentTenant && !user?.isSuperAdmin && user?.platformRole !== 'SUPPORT';
    // Couleurs de la ville appliquées à toute l'interface
    useEffect(() => {
        const root = document.documentElement;
        const hsl = branded && currentTenant?.primaryColor ? hexToHsl(currentTenant.primaryColor) : null;
        if (hsl) {
            root.style.setProperty('--brand-primary', `${hsl.h} ${hsl.s}% ${hsl.l}%`);
            root.style.setProperty('--brand-primary-dark', `${hsl.h} ${hsl.s}% ${Math.max(15, hsl.l - 12)}%`);
            root.style.setProperty('--brand-primary-light', `${hsl.h} ${hsl.s}% ${Math.min(85, hsl.l + 14)}%`);
        }
        else {
            root.style.removeProperty('--brand-primary');
            root.style.removeProperty('--brand-primary-dark');
            root.style.removeProperty('--brand-primary-light');
        }
    }, [branded, currentTenant?.primaryColor]);
    return (_jsx(TenantContext.Provider, { value: { currentTenant, tenants, loading: isLoading, switchTenant, role, branded }, children: children }));
}
export function useTenant() {
    const ctx = useContext(TenantContext);
    if (!ctx)
        throw new Error('useTenant must be used within TenantProvider');
    return ctx;
}
//# sourceMappingURL=TenantContext.js.map